<script>
  import moment from 'moment';
  import { parse } from 'query-string';

  import { onMount } from 'svelte';
  import { navigate, Link } from 'svelte-routing';
  import { apiCall, apiGet, apiUpdate, apiCreate, notify } from '../httpCommon';
  import Paper, { Title, Content as PaperContent } from '@smui/paper';
  import Card, { Content, Actions, ActionIcons } from '@smui/card';
  import IconButton from '@smui/icon-button';
  import Button, { Label, Icon } from '@smui/button';
  import FormField from '@smui/form-field';
  import Switch from '@smui/switch';
  import Radio from '@smui/radio';
  import { exclude } from '@smui/common/exclude';
  import { Textfield, Textshow, RichEdit, ResourceShow } from '../components';

  import ResourceSelect from '../components/ResourceSelect.svelte';
  import CateringRequirements from './CateringRequirements.svelte';

  const resource = 'popups';
  const popupTypes = ['popup', 'catered'];
  const basePath = `/${resource}`;
  let defaultData = {
    popup_type: 'popup',
    status: 'proposed',
    vat_inclusive: true,
  };

  export let location;
  export let id;
  export let title;
  export let topLevel = false;

  let data = null;
  let catered_value_label;

  let errors = {};
  let invalid = {};

  $: isCreate = id === 'create';

  $: {
    if (isCreate) {
      title = 'Create Event';
    } else if (data) {
      title = `${data.vendor_name || ''} @ ${data.venue_name || ''}`;
    } else {
      title = '';
    }
  }

  async function setCommissionRate(e) {
    const vendor = await apiGet('/vendors', e.target.value);
    data.commission_rate_pct = vendor.commission_rate_pct;
  }

  function perHeadUpdated(e) {
    let newValue = null;
    if (data.per_head_value && data.meal_prep) {
      newValue = (data.per_head_value * data.meal_prep).toFixed(2);
    }
    if (newValue !== data.catered_value) {
      data.catered_value = newValue;
    }
  }

  function cateredValueUpdated(e) {
    let newValue = null;
    if (data.catered_value && data.meal_prep) {
      newValue = (data.catered_value / data.meal_prep).toFixed(2);
    }
    if (newValue !== data.per_head_value) {
      data.per_head_value = newValue;
    }
  }

  function mealCountUpdated(e) {
    perHeadUpdated();
  }

  function show() {
    navigate(`${basePath}/${id}`);
  }

  onMount(() => {
    if (isCreate) {
      const searchData = location.search ? exclude(parse(location.search), ['status']) : {};
      data = { ...defaultData, ...searchData };

      data.online_order_allowed = true;
      data.pickup_time_allowed = false;
    }
  });

  $: if (data && data.popup_type === 'catered') {
    if (data.vat_inclusive === null) {
      data.vat_inclusive = true;
    }
    if (data.vat_inclusive) {
      catered_value_label = `Gross Value (incl VAT)`;
    } else {
      catered_value_label = `Net Value (excl VAT)`;
    }
  }
  let setup_mins_helper, duration_mins_helper;
  $: {
    if (data && data.start_time && data.setup_mins) {
      const arrival = moment(data.start_time).subtract(data.setup_mins, 'minutes').format('HH:mm');
      setup_mins_helper = `Arrival Time: ${arrival}`;
    } else {
      setup_mins_helper = 'minutes';
    }
  }
  $: {
    if (data && data.start_time && data.duration_mins) {
      const arrival = moment(data.start_time).add(data.duration_mins, 'minutes').format('HH:mm');
      duration_mins_helper = `Service End Time: ${arrival}`;
    } else {
      duration_mins_helper = 'minutes';
    }
  }

  const toggle_popup_active = () => {
    data.popup_active = data.popup_active ? true : false;

    console.log(data.popup_active);
  };
</script>

<ResourceShow resource="popups" bind:data bind:errors bind:id on:created={show} on:updated={show} {topLevel}>
  <div slot="content" class="flex wrap">
    <div class="card wide">
      <Card variant="outlined" padded>
        {#if data}
          <div class="header">
            {#if data.popup_type === 'popup'}
              <div class="popup-status {data.popup_active ? 'active' : 'inactive'}">
                <FormField>
                  <Switch value={true} bind:checked={data.popup_active} />
                  <span slot="label">{data.popup_active ? 'Popup active' : 'Popup inactive'}</span>
                </FormField>
              </div>
              <div style="margin-top: 20px">
                <FormField>
                  <Switch value={true} bind:checked={data.menu_ordering_active} />
                  <span slot="label">{data.menu_ordering_active ? 'Menu ordering active' : 'Menu ordering disabled'}</span>
                </FormField>
              </div>
            {/if}
          </div>
          <p class="flex no-spread">
            {#if isCreate}
              <ResourceSelect bind:value={data.venue_id} resource="venues" nameField="venue_name" error={errors.venue_id} label="Venue" />
            {:else}
              <Textshow label="Venue">
                <Link to="/venues/{data.venue_id}">{data.venue_name}</Link>
              </Textshow>
            {/if}
          </p>

          <Textfield
            class="short"
            type="datetime-local"
            value={data.start_time ? moment(data.start_time).format('YYYY-MM-DDTHH:mm') : ''}
            error={errors.start_time}
            on:change={(e) => {
              data.start_time = moment(e.target.value).toISOString();
            }}
            label="Service Start Time"
          />
          <Textfield type="number" class="number" bind:value={data.setup_mins} error={errors.setup_mins} label="Setup time" helperText={setup_mins_helper} helperPersistent />
          <p />
          <Textfield type="number" class="number" bind:value={data.duration_mins} error={errors.duration_mins} label="Duration" helperText={duration_mins_helper} helperPersistent />
          <p />

          {#if isCreate || data.status === 'proposed'}
            <ResourceSelect bind:value={data.vendor_id} resource="vendors" nameField="vendor_name" label="Vendor" on:change={setCommissionRate} />
            <p />
          {:else}
            <Textshow label="Vendor">
              <Link to="/vendors/{data.vendor_id}">{data.vendor_name}</Link>
            </Textshow>
          {/if}

          <Textfield type="number" class="number" bind:value={data.commission_rate_pct} error={errors.commission_rate_pct} label="Commission (%)" />
          <p>
            {#if isCreate || data.status === 'proposed'}
              {#each popupTypes as popupType}
                <FormField>
                  <Radio bind:group={data.popup_type} value={popupType} error />
                  <span slot="label">{popupType}</span>
                </FormField>
              {/each}
            {:else}
              <Textshow label="Event Style" value={data.popup_type} />
            {/if}
          </p>
          {#if data.popup_type === 'popup'}
            <FormField>
              <Switch value={true} bind:checked={data.online_order_allowed} />
              <span slot="label">Enable Online Ordering</span>
            </FormField>
            <p />
            <FormField>
              <Switch value={true} bind:checked={data.pickup_time_allowed} />
              <span slot="label">Allow pickup time selection</span>
            </FormField>
            <p />
          {/if}
          <Textfield type="number" class="number" updateInvalid={false} on:change={mealCountUpdated} bind:value={data.meal_prep} error={errors.meal_prep} label="Meal Count" />

          {#if data.popup_type === 'catered'}
            <Textfield class="short" type="number" on:change={perHeadUpdated} bind:value={data.per_head_value} error={errors.per_head_value} label="Amount per Head" />
            <p class="flex no-spread">
              <Textfield class="short" type="number" on:change={cateredValueUpdated} bind:value={data.catered_value} error={errors.catered_value} label={catered_value_label} />
              <FormField>
                <Switch value={true} bind:checked={data.vat_inclusive} />
                <span slot="label">VAT Inclusive</span>
              </FormField>
            </p>
            {#if data && data.popup_type === 'catered' && data.per_head_value && data.catered_value}
              <p>
                {data.meal_prep} @ {data.per_head_value} / head = {data.catered_value}
                ({data.vat_inclusive ? 'incl' : 'excl'} VAT)
              </p>
            {/if}
          {/if}
          <Textfield textarea fullwidth bind:value={data.notes} error={errors.notes} label="Event Notes" />
          {#if data && data.popup_type === 'catered'}
            <CateringRequirements bind:data={data.catering_requirements} errors={errors && errors.catering_requirements ? errors.catering_requirements : {}} />
          {/if}
        {/if}
      </Card>
    </div>
  </div>
</ResourceShow>

<style>
  .popup-status {
    padding: 15px;
    border-radius: 4px;
  }

  .popup-status label span {
    color: #ffffff !important;
    font-weight: bold;
  }

  .popup-status.active {
    background-color: rgba(52, 211, 153, 1);
  }
  .popup-status.inactive {
    background-color: rgba(248, 113, 113, 1);
  }
</style>
